import { useState } from 'react'
import { Box, Flex, Text } from '@chakra-ui/react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  LuClock as Clock,
  LuChevronDown as ChevronDown,
  LuChevronLeft as ChevronLeft,
  LuChevronRight as ChevronRight,
} from 'react-icons/lu'
import { brand, themeColors } from '../../../../theme'
import { parseUtcDate } from '../../../../lib/dates'
import type { EnrichedPoint } from '../../../../lib/trackAnalytics'
import { CARD_SHADOW } from './cardStyles'

const MotionBox = motion.create(Box)
const colors = themeColors
const PAGE_SIZE = 25

function dateTime(iso: string): string {
  const d = parseUtcDate(iso)
  return (
    d.toLocaleDateString('es-PE', { day: '2-digit', month: 'short' }) +
    ' ' +
    d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  )
}

interface Props {
  points: EnrichedPoint[]
  selection: { a: number | null; b: number | null }
  delay?: number
}

export default function GpsPointsTable({ points, selection, delay = 0 }: Props) {
  const [open, setOpen] = useState(true)
  const [page, setPage] = useState(0)

  const total = points.length
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const safePage = Math.min(page, totalPages - 1)
  const start = safePage * PAGE_SIZE
  const pageRows = points.slice(start, start + PAGE_SIZE)

  const segMin = selection.a != null && selection.b != null ? Math.min(selection.a, selection.b) : null
  const segMax = selection.a != null && selection.b != null ? Math.max(selection.a, selection.b) : null
  const inSegment = (i: number) => segMin != null && segMax != null && i >= segMin && i <= segMax

  const headers = ['#', 'Fecha y Hora', 'Latitud', 'Longitud', 'Altitud', 'Velocidad', 'Precisión']

  return (
    <MotionBox
      bg={colors.bgPanel}
      border="1px solid"
      borderColor={colors.border}
      borderRadius="2xl"
      overflow="hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay }}
      boxShadow={CARD_SHADOW}
    >
      <Flex
        as="button"
        width="100%"
        p={5}
        borderBottom={open ? '1px solid' : 'none'}
        borderColor={colors.border}
        alignItems="center"
        justifyContent="space-between"
        cursor="pointer"
        _hover={{ bg: colors.bgHover }}
        transition="background 0.15s"
        onClick={() => setOpen((v) => !v)}
      >
        <Flex alignItems="center" gap={2}>
          <Clock size={16} color={brand.green} />
          <Text fontSize="md" fontWeight="bold" color={colors.text}>
            Registro de Puntos GPS
          </Text>
          <Text fontSize="xs" color={colors.textMuted} ml={2}>
            {total} puntos
          </Text>
        </Flex>
        <MotionBox
          color={colors.textMuted}
          display="flex"
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown size={18} />
        </MotionBox>
      </Flex>

      <AnimatePresence initial={false}>
        {open && (
          <MotionBox
            key="gps-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
          <Box overflowX="auto">
            <Box as="table" width="100%" textAlign="left">
              <Box as="thead">
                <Box as="tr" bg={colors.bgHover}>
                  {headers.map((h) => (
                    <Box
                      as="th"
                      key={h}
                      px={5}
                      py={3}
                      fontSize="10px"
                      textTransform="uppercase"
                      letterSpacing="0.2em"
                      fontWeight="bold"
                      color={colors.textMuted}
                    >
                      {h}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box as="tbody">
                {pageRows.map((p) => {
                  const i = p.index
                  const isFirst = i === 0
                  const isLast = i === total - 1
                  const highlighted = inSegment(i)
                  return (
                    <Box
                      as="tr"
                      key={i}
                      borderBottom="1px solid"
                      borderColor={colors.border}
                      bg={highlighted ? `${brand.gold}14` : undefined}
                      _hover={{ bg: highlighted ? `${brand.gold}22` : colors.bgHover }}
                      transition="background 0.2s"
                    >
                      <Box as="td" px={5} py={3}>
                        <Flex alignItems="center" gap={2}>
                          <Box
                            width="8px"
                            height="8px"
                            borderRadius="full"
                            bg={isFirst ? '#2a8a87' : isLast ? '#3d6fa8' : brand.green}
                            flexShrink={0}
                          />
                          <Text fontSize="sm" fontFamily="monospace" color={colors.text}>
                            {i + 1}
                          </Text>
                        </Flex>
                      </Box>
                      <Box as="td" px={5} py={3}>
                        <Text fontSize="sm" color={colors.text}>
                          {dateTime(p.capturedAt)}
                        </Text>
                      </Box>
                      <Box as="td" px={5} py={3}>
                        <Text fontSize="sm" fontFamily="monospace" color={colors.textSecondary}>
                          {p.lat.toFixed(6)}
                        </Text>
                      </Box>
                      <Box as="td" px={5} py={3}>
                        <Text fontSize="sm" fontFamily="monospace" color={colors.textSecondary}>
                          {p.lng.toFixed(6)}
                        </Text>
                      </Box>
                      <Box as="td" px={5} py={3}>
                        <Text fontSize="sm" fontFamily="monospace" color={colors.textSecondary}>
                          {p.altitude != null ? `${p.altitude.toFixed(0)} m` : '-'}
                        </Text>
                      </Box>
                      <Box as="td" px={5} py={3}>
                        <Text fontSize="sm" fontFamily="monospace" color={colors.textSecondary}>
                          {`${p.speedKmh.toFixed(1)} km/h`}
                        </Text>
                      </Box>
                      <Box as="td" px={5} py={3}>
                        <Text fontSize="sm" fontFamily="monospace" color={colors.textSecondary}>
                          {p.accuracy != null ? `${p.accuracy.toFixed(0)} m` : '-'}
                        </Text>
                      </Box>
                    </Box>
                  )
                })}
              </Box>
            </Box>
          </Box>

          {/* Paginación local */}
          <Flex
            px={5}
            py={3}
            borderTop="1px solid"
            borderColor={colors.border}
            alignItems="center"
            justifyContent="space-between"
            gap={3}
            flexWrap="wrap"
          >
            <Text fontSize="xs" color={colors.textMuted}>
              Mostrando {total === 0 ? 0 : start + 1} – {Math.min(start + PAGE_SIZE, total)} de {total}
            </Text>
            <Flex alignItems="center" gap={2}>
              <PagerButton disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>
                <ChevronLeft size={16} />
              </PagerButton>
              <Text fontSize="xs" color={colors.text} fontWeight="600">
                {safePage + 1} / {totalPages}
              </Text>
              <PagerButton disabled={safePage >= totalPages - 1} onClick={() => setPage(safePage + 1)}>
                <ChevronRight size={16} />
              </PagerButton>
            </Flex>
          </Flex>
          </MotionBox>
        )}
      </AnimatePresence>
    </MotionBox>
  )
}

function PagerButton({ disabled, onClick, children }: { disabled: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <Box
      as="button"
      p={1.5}
      borderRadius="md"
      cursor={disabled ? 'not-allowed' : 'pointer'}
      opacity={disabled ? 0.4 : 1}
      color={colors.textMuted}
      _hover={disabled ? {} : { bg: colors.bgHover, color: colors.text }}
      transition="all 0.15s"
      onClick={disabled ? undefined : onClick}
    >
      {children}
    </Box>
  )
}
